import { Checkbox, Form, Input, Select, Tag } from 'antd'
import { captureException } from '@tucan/utils/error-tracking'
import debug from 'debug'
import React, { JSX } from 'react'

const logger = debug('setting.registry')

const process = (value: string) => {
  let processed

  try {
    processed = JSON.parse(value)
  } catch (e) {
    processed = String(value)
  }

  return processed
}

export const getSettingValue = <A,>(name: string, value?: string): A => {
  if (value === undefined || value === null) {
    return settingRegistry[name]?.default as A
  }

  try {
    const processed = process(value)

    if (processed === undefined || processed === null) {
      return settingRegistry[name]?.default as A
    }

    return processed
  } catch (e) {
    captureException(e)
    logger('using default setting for %s', name)
    return settingRegistry[name]?.default as A
  }
}

export enum Type {
  checkbox,
  select,
  input,
}

interface InputSetting {
  type: Type.input
  default: string
}

interface CheckboxSetting {
  type: Type.checkbox
  default: boolean
}

interface SelectSetting {
  type: Type.select
  default: string | string[]
  mode?: 'tags' | 'multiple'
  options: Array<{
    value: string
    label?: string
  }>
}

type Setting = CheckboxSetting | SelectSetting | InputSetting

interface SettingRegistry {
  [name: string]: Setting
}

export const settingRegistry: SettingRegistry = {
  'allow-group-managers-member-management': {
    default: false,
    type: Type.checkbox,
  },

  'allow-group-managers-search-company-users': {
    default: false,
    type: Type.checkbox,
  },

  'strict-group-permissions': {
    default: false,
    type: Type.checkbox,
  },

  'strict-group-permissions-not-for-admin': {
    default: false,
    type: Type.checkbox,
  },

  ask_to_delete_transcript: {
    default: false,
    type: Type.checkbox,
  },

  dct_transcription: {
    default: false,
    type: Type.checkbox,
  },

  date_format: {
    default: 'YYYY-MM-DD',
    options: [
      { value: 'YYYY-MM-DD' },
      { value: 'DD-MM-YYYY' },
      { value: 'MM-dd-yyyy' },
      { value: 'MMMM Do YYYY' },
    ],
    type: Type.select,
  },

  force_logout_after_min_inactive: {
    default: '0',
    type: Type.input,
  },

  force_logout_on_reopen: {
    default: false,
    type: Type.checkbox,
  },

  meeting_bot: {
    default: false,
    type: Type.checkbox,
  },

  meeting_default_overview: {
    default: '',
    options: [
      { label: 'calendar', value: 'calendar' },
      { label: 'list', value: 'list' },
    ],
    type: Type.select,
  },

  tag_extractor: {
    default: 'keywords',
    options: [{ value: 'keywords' }, { value: 'legacy' }, { value: 'ml' }],
    type: Type.select,
  },

  time_format: {
    default: 'HH:mm',
    options: [
      { label: '24H', value: 'HH:mm' },
      { label: 'AM/PM', value: 'hh:mm a' },
    ],
    type: Type.select,
  },

  transcript_default_insight_cards: {
    default: ['overview', 'insights', 'tags', 'speakers', 'word-count', 'tasks'],
    mode: 'multiple',
    options: [
      { label: 'transcript-information-card', value: 'overview' },
      { label: 'insight-insights-card', value: 'insights' },
      { label: 'insight-tags-card', value: 'tags' },
      { label: 'insight-speakers-card', value: 'speakers' },
      { label: 'insight-words-card', value: 'word-count' },
      { label: 'insight-tasks-card', value: 'tasks' },
    ],
    type: Type.select,
  },

  transcript_dictate_default: {
    default: false,
    type: Type.checkbox,
  },

  transcript_editor_autoscroll_default: {
    default: false,
    type: Type.checkbox,
  },

  transcript_editor_version: {
    default: 'v1',
    options: [{ value: 'v1' }, { value: 'v2' }],
    type: Type.select,
  },

  transcript_underline_default: {
    default: false,
    type: Type.checkbox,
  },
}

export const previewSettingValue = (
  setting: { name: string; value?: string },
  t: (key: string, fallback: string) => string
): JSX.Element | string | null => {
  if (!setting) {
    return null
  }
  const definition = settingRegistry[setting.name]

  switch (definition?.type) {
    case Type.checkbox:
      return getSettingValue<boolean>(setting.name, setting.value) ? (
        <Tag color="green">{t('tag-yes', 'YES')}</Tag>
      ) : (
        <Tag color="red">{t('tag-no', 'NO')}</Tag>
      )

    case Type.select:
      const values = getSettingValue<string[]>(setting.name, setting.value)

      return definition.options
        .filter((option) => String(values).includes(option.value))
        .map((option) => {
          if (option.label) {
            return t(option.label, option.label)
          }

          return option.value
        })
        .join(', ')
  }

  return <div>{t('setting-hidden', 'Hidden')}</div>
}

export const settingFormItem = (
  setting: string,
  t: (key: string, fallback: string) => string
): JSX.Element => {
  const definition = settingRegistry[setting]

  switch (definition.type) {
    case Type.checkbox:
      return (
        <Form.Item
          valuePropName="checked"
          name="value"
          label={t('company-setting-value', 'Value')}
          initialValue={definition.default}
        >
          <Checkbox />
        </Form.Item>
      )
    case Type.select:
      return (
        <Form.Item
          name="value"
          label={t('company-setting-value', 'Value')}
          initialValue={definition.default}
        >
          <Select mode={definition.mode} options={definition.options ?? []} />
        </Form.Item>
      )
    default:
      return (
        <Form.Item
          name="value"
          label={t('company-setting-value', 'Value')}
          initialValue={definition.default}
        >
          <Input />
        </Form.Item>
      )
  }
}
